import React, { useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';

/**
 * 📰 VERDICT POSTER (KILLER_MODE spec §5.4, the share loop) — whodunnit's
 * closing end-card: the case, the truth in the culprit's own handwriting
 * (madlib_reveal), who got charged vs who did it, the chair verdict,
 * confessed secrets, and YOUR outcome as a rubber stamp.
 *
 * Fetch-free by contract: everything renders from the status payload + users
 * roster. This is the first consumer of `madlib_reveal` and the persisted
 * `chair` block — both are PUBLIC only once the finale ran (backend doctrine),
 * so a truthy accusation_result is the mount gate, not a secrecy risk.
 */

const PAPER = '#efe6cf';
const INK = '#1c1710';
const INK_SOFT = '#5a4d33';
const RULE = '#9c8a5e';
const SCRIPT_FONT = "'Bradley Hand', 'Segoe Script', 'Comic Sans MS', cursive";
const SERIF = "Georgia, 'Times New Roman', serif";

function normName(s) { return String(s || '').trim().toLowerCase(); }

export default function VerdictPoster({ status, users, humanPlayerId, onClose }) {
    const posterRef = useRef(null);
    const [busy, setBusy] = useState(null); // 'copy' | 'save'
    const [copied, setCopied] = useState(false);

    const scenario = status?.scenario || null;
    const result = status?.accusation_result || {};
    const killerName = status?.killer_revealed?.name || 'Unknown';
    const killerId = status?.killer_revealed?.id;
    const killerCharged = !!result.killer_charged;
    const chargedName = result.charged_name || null;
    const culprit = scenario?.culprit_noun || 'the killer';

    const self = (users || []).find(u => u.game_player_id === humanPlayerId || u.id === humanPlayerId);
    const youDidIt = !!status?.you_did_it;
    const youWereCharged = !!(self && chargedName && normName(self.name) === normName(chargedName));

    // The truth, in the culprit's own handwriting (madlib picks fill the line).
    const truthLine = useMemo(() => {
        const r = status?.madlib_reveal;
        if (!r) return null;
        const picks = [r.weapon, r.place, r.left_behind];
        if (r.line && r.line.includes('___')) {
            let i = 0;
            return r.line.replace(/___/g, () => picks[i++] || '...');
        }
        return `I did it with ${r.weapon} at ${r.place}, and like an idiot I left ${r.left_behind} behind.`;
    }, [status?.madlib_reveal]);

    const stamp = useMemo(() => {
        if (!self) return null;
        if (youDidIt) {
            return killerCharged
                ? { text: 'CAUGHT', color: '#b3261e' }
                : { text: 'GOT AWAY WITH IT', color: '#a8871f' };
        }
        if (youWereCharged && !killerCharged) return { text: 'WRONGLY ACCUSED', color: '#b3261e' };
        return { text: 'WALKED FREE', color: '#2e6b30' };
    }, [self, youDidIt, killerCharged, youWereCharged]);

    const headline = killerCharged
        ? `${killerName.toUpperCase()} DID IT.`
        : (chargedName ? `THEY CHARGED ${chargedName.toUpperCase()}.` : 'NOBODY WAS CHARGED.');
    const subline = killerCharged
        ? `Booked at dawn. The precinct got ${culprit}.`
        : `Wrong call. ${killerName} walked out the back.`;

    const confessions = useMemo(() => (status?.confessed_secrets || []).map(s => {
        const u = (users || []).find(x => String(x.id) === String(s.player_id) || String(x.game_player_id) === String(s.player_id));
        return { name: u?.name || 'Somebody', text: s.text, emoji: s.emoji || '🤫' };
    }), [status?.confessed_secrets, users]);

    const chair = status?.chair || null;
    const confessedIds = useMemo(() => new Set((status?.confessed_secrets || []).map(s => String(s.player_id))), [status?.confessed_secrets]);

    const cast = useMemo(() => (users || [])
        .filter(u => !u.data?.is_spectator)
        .slice(0, 8)
        .map(u => {
            const isKiller = killerId != null && (String(u.id) === String(killerId) || String(u.game_player_id) === String(killerId));
            const isCharged = chargedName && normName(u.name) === normName(chargedName);
            const isChair = chair && (String(u.id) === String(chair.player_id) || String(u.game_player_id) === String(chair.player_id));
            const badge = isKiller ? '🔪' : (isCharged ? '🚔' : (isChair ? (chair.verdict === 'smooth' ? '🧊' : '💦') : (confessedIds.has(String(u.id)) || confessedIds.has(String(u.game_player_id)) ? '🤫' : null)));
            return { key: u.id, name: u.name, img: u.profile_image_url || u.data?.profile_image_url || null, badge, isKiller };
        }), [users, killerId, chargedName, chair, confessedIds]);

    const capture = async () => {
        const canvas = await html2canvas(posterRef.current, { scale: 2, useCORS: true, backgroundColor: null });
        return canvas;
    };
    const doSave = async () => {
        if (busy) return;
        setBusy('save');
        try {
            const canvas = await capture();
            const a = document.createElement('a');
            a.href = canvas.toDataURL('image/png');
            a.download = `case-closed-${status?.game_id || 'whodunnit'}.png`;
            a.click();
        } catch (e) {
            console.error('[VerdictPoster] save failed', e);
        } finally { setBusy(null); }
    };
    const doCopy = async () => {
        if (busy) return;
        setBusy('copy');
        try {
            const canvas = await capture();
            const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
            await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (e) {
            // Clipboard image write is flaky cross-browser; the download IS the fallback.
            console.warn('[VerdictPoster] copy failed, downloading instead', e);
            setBusy(null);
            doSave();
            return;
        }
        setBusy(null);
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 200000,
                background: 'rgba(5,5,10,0.82)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px', overflowY: 'auto',
            }}
        >
            <div onClick={e => e.stopPropagation()} style={{ maxHeight: '94dvh', overflowY: 'auto', borderRadius: '6px' }}>
                {/* The paper — everything inside posterRef is what gets captured */}
                <div
                    ref={posterRef}
                    style={{
                        width: 'min(94vw, 400px)', boxSizing: 'border-box',
                        background: `linear-gradient(160deg, ${PAPER}, #e2d5b4)`,
                        color: INK, fontFamily: SERIF,
                        padding: '18px 20px 14px', position: 'relative',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                        animation: 'verdictPosterIn 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.05)',
                    }}
                >
                    {/* Masthead */}
                    <div style={{ textAlign: 'center', letterSpacing: '3px', fontWeight: 700, fontSize: '0.78em', color: INK_SOFT }}>
                        ★ THE DAILY PRECINCT ★
                    </div>
                    <div style={{ borderTop: `2px solid ${INK}`, borderBottom: `1px solid ${INK}`, margin: '6px 0 10px', padding: '3px 0', textAlign: 'center', fontSize: '0.68em', letterSpacing: '1.5px', color: INK_SOFT }}>
                        {(scenario?.name || 'THE CLASSIC CASE').toUpperCase()} · FINAL EDITION
                    </div>

                    {/* Headline */}
                    <div style={{ textAlign: 'center', fontWeight: 900, fontSize: '1.75em', lineHeight: 1.05, margin: '4px 0 6px' }}>
                        {headline}
                    </div>
                    <div style={{ textAlign: 'center', fontStyle: 'italic', fontSize: '0.88em', color: INK_SOFT, marginBottom: '12px' }}>
                        {subline}
                    </div>

                    {/* Victim obituary (scenario packs carry one; classic skips it) */}
                    {scenario?.victim?.name && (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', border: `1px solid ${RULE}`, padding: '8px 10px', marginBottom: '12px', background: 'rgba(255,255,255,0.25)' }}>
                            <span style={{ fontSize: '1.8em' }}>{scenario.victim.emoji || '🕯️'}</span>
                            <div style={{ fontSize: '0.8em', lineHeight: 1.35 }}>
                                <b>{scenario.victim.name}</b>{scenario.victim.age ? `, ${scenario.victim.age}` : ''}.{' '}
                                <span style={{ fontStyle: 'italic', color: INK_SOFT }}>{scenario.victim.obituary}</span>
                            </div>
                        </div>
                    )}

                    {/* Cast strip, mugshot style */}
                    {cast.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                            {cast.map(c => (
                                <div key={c.key} style={{ textAlign: 'center', width: '54px' }}>
                                    <div style={{ position: 'relative', width: '44px', height: '44px', margin: '0 auto', borderRadius: '4px', overflow: 'hidden', border: `2px solid ${c.isKiller ? '#b3261e' : RULE}`, background: '#d8c9a5' }}>
                                        {c.img ? (
                                            <img src={c.img} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'sepia(0.55) contrast(1.05)' }} />
                                        ) : (
                                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: INK_SOFT, fontSize: '1.2em' }}>
                                                {(c.name || '?').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        {c.badge && (
                                            <span style={{ position: 'absolute', bottom: '-2px', right: '-2px', fontSize: '0.95em', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))' }}>{c.badge}</span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '0.55em', marginTop: '3px', fontWeight: 700, color: INK_SOFT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* The truth, in their own handwriting */}
                    {truthLine && (
                        <div style={{ border: `2px dashed ${RULE}`, padding: '10px 12px', marginBottom: '12px', background: 'rgba(255,255,255,0.3)' }}>
                            <div style={{ fontSize: '0.62em', letterSpacing: '2px', fontWeight: 700, color: INK_SOFT, marginBottom: '5px' }}>
                                THE TRUTH, IN THEIR OWN HANDWRITING
                            </div>
                            <div style={{ fontFamily: SCRIPT_FONT, fontSize: '1.02em', lineHeight: 1.4 }}>
                                &ldquo;{truthLine}&rdquo;
                            </div>
                            <div style={{ fontFamily: SCRIPT_FONT, textAlign: 'right', fontSize: '0.9em', marginTop: '4px' }}>
                                signed, {killerName}
                            </div>
                        </div>
                    )}

                    {/* Small print: chair verdict + confessions */}
                    {(chair || confessions.length > 0) && (
                        <div style={{ fontSize: '0.72em', lineHeight: 1.5, color: INK_SOFT, borderTop: `1px solid ${RULE}`, paddingTop: '8px', marginBottom: '8px' }}>
                            {chair && (
                                <div>🪑 {chair.name} took the chair. {chair.verdict === 'smooth' ? '🧊 Cool as ice.' : '💦 Sweated through it.'}</div>
                            )}
                            {confessions.map((c, i) => (
                                <div key={i}>{c.emoji} {c.name} came clean: <span style={{ fontStyle: 'italic' }}>{c.text}</span></div>
                            ))}
                        </div>
                    )}

                    {/* YOUR stamp */}
                    {stamp && (
                        <div style={{
                            position: 'absolute', top: '38%', left: '50%',
                            transform: 'translate(-50%, -50%) rotate(-12deg)',
                            border: `4px solid ${stamp.color}`, color: stamp.color,
                            padding: '6px 14px', fontWeight: 900, fontSize: '1.5em',
                            letterSpacing: '2px', whiteSpace: 'nowrap', borderRadius: '4px',
                            opacity: 0.82, pointerEvents: 'none',
                            fontFamily: SERIF, textTransform: 'uppercase',
                            mixBlendMode: 'multiply',
                        }}>
                            {stamp.text}
                        </div>
                    )}

                    <div style={{ textAlign: 'center', fontSize: '0.58em', letterSpacing: '2px', color: INK_SOFT, borderTop: `2px solid ${INK}`, paddingTop: '5px' }}>
                        WHODUNNIT · myspaice.ai
                    </div>
                </div>

                {/* Controls live OUTSIDE the captured node */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', padding: '12px 0 4px' }}>
                    <button onClick={doCopy} disabled={!!busy} style={btnStyle(busy === 'copy')}>
                        {copied ? '✓ Copied' : (busy === 'copy' ? 'Copying...' : '📋 Copy image')}
                    </button>
                    <button onClick={doSave} disabled={!!busy} style={btnStyle(busy === 'save')}>
                        {busy === 'save' ? 'Saving...' : '⬇️ Save'}
                    </button>
                    <button onClick={onClose} style={btnStyle(false)}>✕ Close</button>
                </div>
            </div>
            <style>{`
                @keyframes verdictPosterIn {
                    from { opacity: 0; transform: translateY(28px) rotate(-1.5deg); }
                    to { opacity: 1; transform: translateY(0) rotate(0deg); }
                }
            `}</style>
        </div>
    );
}

function btnStyle(busy) {
    return {
        padding: '9px 16px', borderRadius: '10px', cursor: busy ? 'wait' : 'pointer',
        border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.1)',
        color: '#fff', fontWeight: 700, fontSize: '0.85em',
    };
}
